import { Col, Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";

import { getFirstAvailable } from "helpers/commonHelpers";
import ShippingPartyDetails from "pages/common/ShipmentTrackingV2/components/InvolvedParties/ShippingPartyDetails";
import Hr from "components/atoms/Hr/Hr";
import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";

type QuoteShippingPartyDetailsProps = {
  consignee: any;
  notifyParty: any;
};

function formatPortValue(party: any) {
  const airportName = party?.airport?.name;
  const airportCode = party?.airport?.code;
  const seaportName = party?.seaport?.name;
  const seaportCode = party?.seaport?.un_locode;
  if (airportName || airportCode) return `${airportName || ""}${airportCode ? ` (${airportCode})` : ""}`.trim();
  if (seaportName || seaportCode) return `${seaportName || ""}${seaportCode ? ` (${seaportCode})` : ""}`.trim();
  return "";
}

function getPortCountry(party: any) {
  return party?.airport?.country_name || party?.seaport?.country_name || "";
}

function hasPortDetails(party: any) {
  return !!(party?.airport || party?.seaport);
}

function getPortLabelByType(party: any, partyName: "consignee" | "notifyParty") {
  const isAirport = !!party?.airport;
  if (partyName === "consignee") {
    return isAirport ? (
      <FormattedMessage id="quote-request-detail.consigneeAirport" defaultMessage="Consignee airport" />
    ) : (
      <FormattedMessage id="quote-request-detail.consigneeSeaport" defaultMessage="Consignee seaport" />
    );
  }
  return isAirport ? (
    <FormattedMessage id="quote-request-detail.notifyPartyAirport" defaultMessage="Notify party airport" />
  ) : (
    <FormattedMessage id="quote-request-detail.notifyPartySeaport" defaultMessage="Notify party seaport" />
  );
}

const QuoteShippingPartyDetails = function (props: QuoteShippingPartyDetailsProps) {
  const { consignee, notifyParty } = props;
  const isPortAddressDetailRenderingEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_PORT_ADDRESS_DETAIL_RENDERING);
  const isConsigneePortAddress = isPortAddressDetailRenderingEnabled && !!consignee?.is_port_address;
  const isNotifyPartyPortAddress = isPortAddressDetailRenderingEnabled && !!notifyParty?.is_port_address;

  const consigneeCompanyName = getFirstAvailable(
    consignee?.address?.name,
    consignee?.airport?.name,
    consignee?.seaport?.name,
  );

  const notifyPartyCompanyName = getFirstAvailable(
    notifyParty?.address?.name,
    notifyParty?.airport?.name,
    notifyParty?.seaport?.name,
  );

  return (
    <div>
      {getFirstAvailable(consignee?.address, consignee?.airport, consignee?.seaport) && (
        <div>
          <Hr />
          {consignee.contact_name && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.consigneeContact" defaultMessage="Consignee contact" />
              </Col>
              <Col>{consignee.contact_name}</Col>
            </Row>
          )}
          {consignee.contact_phone && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.consigneePhone" defaultMessage="Consignee phone" />
              </Col>
              <Col>{consignee.contact_phone}</Col>
            </Row>
          )}
          {consignee.contact_email && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.consigneeEmail" defaultMessage="Consignee email" />
              </Col>
              <Col>{consignee.contact_email}</Col>
            </Row>
          )}
          {consigneeCompanyName && !isConsigneePortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.consigneeCompany" defaultMessage="Consignee company" />
              </Col>
              <Col>{consigneeCompanyName}</Col>
            </Row>
          )}
          {(isConsigneePortAddress || hasPortDetails(consignee)) && (
            <Row className="mb-2 ">
              <Col className="text-secondary">{getPortLabelByType(consignee, "consignee")}</Col>
              <Col>{formatPortValue(consignee) || "-"}</Col>
            </Row>
          )}
          {isConsigneePortAddress ? (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.consigneeCountry" defaultMessage="Consignee country" />
              </Col>
              <Col>{getPortCountry(consignee) || "-"}</Col>
            </Row>
          ) : (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.consigneeAddress" defaultMessage="Consignee address" />
              </Col>
              <Col>
                <ShippingPartyDetails data={consignee} showName={false} withoutStyles />
              </Col>
            </Row>
          )}
        </div>
      )}
      {getFirstAvailable(notifyParty?.address, notifyParty?.airport, notifyParty?.seaport) && (
        <div>
          <Hr />
          {notifyParty.contact_name && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.notifyPartyContact" defaultMessage="Notify party contact" />
              </Col>
              <Col>{notifyParty.contact_name}</Col>
            </Row>
          )}
          {notifyParty.contact_phone && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.notifyPartyPhone" defaultMessage="Notify party phone" />
              </Col>
              <Col>{notifyParty.contact_phone}</Col>
            </Row>
          )}
          {notifyParty.contact_email && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.notifyPartyEmail" defaultMessage="Notify party email" />
              </Col>
              <Col>{notifyParty.contact_email}</Col>
            </Row>
          )}
          {notifyPartyCompanyName && !isNotifyPartyPortAddress && (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.notifyPartyCompany" defaultMessage="Notify party company" />
              </Col>
              <Col>{notifyPartyCompanyName}</Col>
            </Row>
          )}
          {(isNotifyPartyPortAddress || hasPortDetails(notifyParty)) && (
            <Row className="mb-2 ">
              <Col className="text-secondary">{getPortLabelByType(notifyParty, "notifyParty")}</Col>
              <Col>{formatPortValue(notifyParty) || "-"}</Col>
            </Row>
          )}
          {isNotifyPartyPortAddress ? (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.notifyPartyCountry" defaultMessage="Notify party country" />
              </Col>
              <Col>{getPortCountry(notifyParty) || "-"}</Col>
            </Row>
          ) : (
            <Row className="mb-2 ">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.notifyPartyAddress" defaultMessage="Notify party address" />
              </Col>
              <Col>
                <ShippingPartyDetails data={notifyParty} showName={false} withoutStyles />
              </Col>
            </Row>
          )}
        </div>
      )}
    </div>
  );
};

export default QuoteShippingPartyDetails;
